import React from 'react';

// Get the header color based on score and invert logic
const getHeaderColor = (score, invertColors) => {
  const lowScore = score < 5;
  const medScore = score > 4 && score < 7;

  if (invertColors) {
    // For saturation: low scores are good (green), high scores are bad (red)
    if (lowScore) return 'text-green-700';
    if (medScore) return 'text-yellow-700';
    return 'text-red-700';
  } else {
    // Normal logic: high scores are good (green), low scores are bad (red)
    if (lowScore) return 'text-red-700';
    if (medScore) return 'text-yellow-700';
    return 'text-green-700';
  }
};

// Helper function to remove duplicate sentences
function removeDuplicateSentences(text) {
    // Split into sentences
    const sentences = text.split(/\.(?:\s+|$)/).filter(s => s.trim());
    
    // Track seen sentences (normalized for comparison)
    const seenSentences = new Set();
    const uniqueSentences = [];
    
    sentences.forEach(sentence => {
      const normalized = sentence.trim().toLowerCase();
      if (normalized && !seenSentences.has(normalized)) {
        seenSentences.add(normalized);
        uniqueSentences.push(sentence.trim());
      }
    });
    
    // Rejoin sentences
    return uniqueSentences.map(s => s.endsWith('.') ? s : s + '.').join(' ');
}

// Helper function to break continuous text into logical sections
function breakContinuousText(text) {
    // First remove duplicate sentences
    const deduplicatedText = removeDuplicateSentences(text);
    
    // Check for numbered lists in the format "1) item, 2) item, 3) item"
    const numberedListPattern = /(\d+\)\s*[^,]+(?:,\s*\d+\)\s*[^,.]+)*)/g;
    const hasNumberedList = numberedListPattern.test(deduplicatedText);
    
    if (hasNumberedList) {
      // Split text around numbered lists
      const parts = [];
      let lastIndex = 0;
      
      deduplicatedText.replace(numberedListPattern, (match, p1, offset) => {
        // Add text before the list
        if (offset > lastIndex) {
          const beforeText = deduplicatedText.substring(lastIndex, offset).trim();
          if (beforeText) {
            parts.push(beforeText);
          }
        }
        
        // Add the numbered list as a separate section
        parts.push(match.trim());
        lastIndex = offset + match.length;
      });
      
      // Add remaining text after the last list
      if (lastIndex < deduplicatedText.length) {
        const afterText = deduplicatedText.substring(lastIndex).trim();
        if (afterText) {
          parts.push(afterText);
        }
      }
      
      return parts.filter(p => p.trim());
    }
    
    // First try specific patterns for structured content
    const specificPatterns = [
      /\.\s+(Key opportunity drivers include:)/gi,
      /\.\s+(Primary challenges include:)/gi,
      /\.\s+(Strategic recommendations would include:)/gi,
      /\.\s+(The market saturation score)/gi,
      /\.\s+(The moderate USP score)/gi
    ];
    
    let sections = [deduplicatedText];
    let hasSpecificPatterns = false;
    
    // Check if text contains any specific patterns
    specificPatterns.forEach(pattern => {
      if (pattern.test(deduplicatedText)) {
        hasSpecificPatterns = true;
      }
    });
    
    // If we have specific patterns, use the original logic
    if (hasSpecificPatterns) {
      specificPatterns.forEach(pattern => {
        const newSections = [];
        sections.forEach(section => {
          const parts = section.split(pattern);
          if (parts.length > 1) {
            if (parts[0].trim()) {
              newSections.push(parts[0].trim());
            }
            for (let i = 1; i < parts.length; i += 2) {
              const header = parts[i];
              const content = parts[i + 1] || '';
              if (header && content) {
                newSections.push(header + content);
              } else if (header) {
                newSections.push(header);
              }
            }
          } else {
            newSections.push(section);
          }
        });
        sections = newSections;
      });
      return sections.filter(section => section.trim());
    }
    
    // For general assessment text, preserve as single paragraph
    // Don't break up continuous assessment text - it reads better as a cohesive block
    return [deduplicatedText];
}

// Helper function to process inline formatting within continuous text
function processInlineFormatting(text) {
    let processedText = text;
    
    // Handle numbered lists with parentheses: "1) item, 2) item, 3) item"
    const parenListMatch = text.match(/(\d+\)\s*[^,]+(?:,\s*\d+\)\s*[^,.]+)*)/);
    if (parenListMatch) {
      const listText = parenListMatch[0];
      const items = listText.split(/,\s*(?=\d+\))/).map(item => item.trim());
      
      if (items.length > 1) {
        const formattedList = items.map(item => {
          // Extract the number and content
          const match = item.match(/^\d+\)\s*(.+)/);
          if (match) {
            return `• ${match[1].trim()}`;
          }
          return `• ${item}`;
        }).join('\n');
        
        processedText = processedText.replace(listText, `\n${formattedList}\n`);
      }
    }
    
    // Handle inline lists for "include:" patterns - improved to handle sentences after lists
    const listPatterns = [
      /(Key opportunity drivers include:|Primary challenges include:)\s*/gi,
      /(Strategic recommendations would include:)\s*/gi
    ];
    
    listPatterns.forEach(pattern => {
      processedText = processedText.replace(pattern, (match, header) => {
        // Find the position after the header
        const headerEnd = processedText.indexOf(match) + match.length;
        const remainingText = processedText.substring(headerEnd);
        
        // For numbered recommendations, look for numbered items pattern
        if (header.toLowerCase().includes('recommendation')) {
          // Match numbered items: "1. text, 2. text, 3. text. Following sentence..."
          const numberedMatch = remainingText.match(/^(\d+\.\s*[^.]+(?:,\s*\d+\.\s*[^.]+)*)\.\s*(.*)$/);
          if (numberedMatch) {
            const listPart = numberedMatch[1];
            const afterList = numberedMatch[2];
            
            // Split numbered items and clean them up
            const numberedItems = listPart.split(/,\s*(?=\d+\.)/).map(item => item.trim());
            if (numberedItems.length > 1) {
              const listItems = numberedItems.map(item => `• ${item.replace(/^\d+\.\s*/, '')}`).join('\n');
              const result = `${header}\n${listItems}`;
              
              // If there's text after the list, add it as a separate paragraph
              if (afterList.trim()) {
                return `${result}\n\n${afterList.trim()}`;
              }
              return result;
            }
          }
        }
        
        // For other lists (comma-separated), find content until the next sentence
        const listMatch = remainingText.match(/^([^.]+)\.\s*(.*)$/);
        if (listMatch) {
          const listContent = listMatch[1];
          const afterList = listMatch[2];
          
          // Split by comma and create bullet points
          const items = listContent.split(/,\s*(?=\w)/).map(item => item.trim().replace(/^and\s+/, '')).filter(item => item);
          if (items.length > 1) {
            const listItems = items.map(item => `• ${item}`).join('\n');
            const result = `${header}\n${listItems}`;
            
            // If there's text after the list, add it as a separate paragraph
            if (afterList.trim()) {
              return `${result}\n\n${afterList.trim()}`;
            }
            return result;
          }
        }
        
        return match;
      });
    });
    
    return processedText;
}

/**
 * Formats assessment reason text for better readability
 * @param {string} text - The raw assessment reason text
 * @param {number} score - The assessment score (1-10) for color theming
 * @param {boolean} invertColors - Whether to invert the color logic (for saturation)
 * @returns {JSX.Element} - Formatted JSX with proper line breaks and structure
 */
export const formatAssessmentReason = (text, score = 7, invertColors = false) => {
  if (!text) return null;

  // Simple formatting: just preserve the text as a single paragraph with no manipulation
  return (
    <p className="leading-relaxed whitespace-pre-wrap">
      {text}
    </p>
  );
};

/**
 * Simple text formatter that preserves line breaks and adds basic formatting
 * @param {string} text - The raw text to format
 * @returns {JSX.Element} - Formatted JSX with line breaks preserved
 */
export const formatTextWithLineBreaks = (text) => {
  if (!text) return null;
  
  return text.split('\n').map((line, index) => (
    <span key={index}>
      {line}
      {index < text.split('\n').length - 1 && <br />}
    </span>
  ));
};